import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { motion } from 'framer-motion';
import axios from 'axios';
import AuthContext from '../context/auth/authContext';
import setAuthToken from '../utils/setAuthToken';
import AuthShell from '../components/auth/AuthShell';

const LoginPage = () => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const { login, googleLogin, error, clearErrors, isAuthenticated, user, loadUser } = authContext;
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) {
        if (user.role === 'admin') {
            navigate('/super-admin');
        } else {
            navigate('/dashboard');
        }
    }
    if (error) {
      if (typeof error === 'string' && error.includes('doğrulanmamış')) {
        if (window.confirm(error + '\n\nDoğrulama e-postasını yeniden göndermek ister misiniz?')) {
          const email = window.prompt('Kayıtlı e-posta adresinizi girin:');
          if (email) {
            fetch('/api/sellers/resend-verification', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email })
            })
              .then(res => res.json())
              .then(data => alert(data.msg))
              .catch(() => alert('Gönderim sırasında bir hata oluştu, lütfen tekrar deneyin.'));
          }
        }
      } else {
        alert(error);
      }
      clearErrors();
    }
    // eslint-disable-next-line
  }, [error, isAuthenticated, user]);

  const [loginTab, setLoginTab] = useState('seller'); // 'seller' | 'staff'
  const [formData, setFormData] = useState({ username: '', password: '' });
  const { username, password } = formData;
  const [totpRequired, setTotpRequired] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [totpSource, setTotpSource] = useState('password'); // 'password' | 'google'
  const [googleCredential, setGoogleCredential] = useState(null);

  // Personel Girişi State'leri
  const [sellerEmail, setSellerEmail] = useState(localStorage.getItem('last_staff_seller_email') || '');
  const [storesList, setStoresList] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [staffForm, setStaffForm] = useState({ store_id: '', username: '', password: '' });
  const [staffLoading, setStaffLoading] = useState(false);

  useEffect(() => {
    if (!sellerEmail || !sellerEmail.trim()) {
      setStoresList([]);
      setStaffForm(prev => ({ ...prev, store_id: '' }));
      return;
    }

    const timer = setTimeout(() => {
      setLoadingStores(true);
      axios.get(`/api/staff/public-stores?email=${encodeURIComponent(sellerEmail.trim())}`)
        .then(res => {
          const list = res.data || [];
          setStoresList(list);
          if (list.length > 0) {
            setStaffForm(prev => ({ ...prev, store_id: list[0].store_id }));
            localStorage.setItem('last_staff_seller_email', sellerEmail.trim());
          } else {
            setStaffForm(prev => ({ ...prev, store_id: '' }));
          }
        })
        .catch(() => {
          setStoresList([]);
          setStaffForm(prev => ({ ...prev, store_id: '' }));
        })
        .finally(() => setLoadingStores(false));
    }, 350);

    return () => clearTimeout(timer);
  }, [sellerEmail]);

  const onChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });
  const onStaffChange = e => setStaffForm({ ...staffForm, [e.target.name]: e.target.value });

  const onSubmit = async e => {
    e.preventDefault();
    if (username === '' || password === '') {
        alert(t('fill_all_fields'));
        return;
    }
    if (totpRequired && !/^\d{6}$/.test(totpCode.trim())) {
        alert('Lütfen doğrulama uygulamasındaki 6 haneli kodu girin.');
        return;
    }
    const payload = { username, password };
    if (totpRequired) payload.totp_code = totpCode.trim();
    const result = await login(payload);
    if (result?.totpRequired) {
        setTotpSource('password');
        setTotpRequired(true);
        if (result.invalid) alert('Doğrulama kodu hatalı, tekrar deneyin.');
    }
  };

  const onGoogleTotpSubmit = async e => {
    e.preventDefault();
    if (!/^\d{6}$/.test(totpCode.trim())) {
        alert('Lütfen doğrulama uygulamasındaki 6 haneli kodu girin.');
        return;
    }
    const result = await googleLogin(googleCredential, totpCode.trim());
    if (result?.totpRequired && result.invalid) {
        alert('Doğrulama kodu hatalı, tekrar deneyin.');
    }
  };

  const cancelGoogleTotp = () => {
    setTotpRequired(false);
    setTotpCode('');
    setGoogleCredential(null);
  };

  const onStaffSubmit = async (e) => {
    e.preventDefault();
    if (!staffForm.store_id || !staffForm.username || !staffForm.password) {
      alert('Lütfen şube seçin, kullanıcı adı ve şifrenizi girin.');
      return;
    }
    setStaffLoading(true);
    try {
      const res = await axios.post('/api/staff/login', staffForm);
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
        setAuthToken(res.data.token);
        loadUser(res.data.token);
      }
    } catch (err) {
      alert(err.response?.data?.msg || 'Personel girişi başarısız.');
    } finally {
      setStaffLoading(false);
    }
  };

  const responseGoogle = async (response) => {
      if (!response.credential) return;
      setGoogleCredential(response.credential);
      const result = await googleLogin(response.credential);
      if (result?.totpRequired) {
          setTotpSource('google');
          setTotpRequired(true);
          if (result.invalid) alert('Doğrulama kodu hatalı, tekrar deneyin.');
      }
  };

  return (
    <AuthShell
      maxWidth={440}
      icon={loginTab === 'seller' ? (totpRequired ? 'shield_lock' : 'lock') : 'badge'}
      title={loginTab === 'seller' ? t('welcome') : 'Personel Girişi'}
      subtitle={loginTab === 'seller' ? t('login_to_admin') : 'Şube ve personel bilgilerinizle sisteme giriş yapın'}
      headerRight={
        <Link to="/register" className="auth-shell-back">
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>person_add</span>
          {t('create_account')}
        </Link>
      }
      footer={
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
          © 2024 QRezervasyon. {t('all_rights_reserved')} <br />
          <button onClick={(e) => e.preventDefault()} style={{ color: '#f97316', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 'inherit' }}>{t('privacy')}</button> {t('or')} <button onClick={(e) => e.preventDefault()} style={{ color: '#f97316', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 'inherit' }}>{t('terms')}</button>
        </p>
      }
    >
      {/* TAB SWITCHER: İşletme Girişi vs Personel Girişi */}
      <div className="auth-tabs">
        <button
          type="button"
          onClick={() => setLoginTab('seller')}
          className={`auth-tab ${loginTab === 'seller' ? 'active' : ''}`}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }}>storefront</span>
          İşletme Girişi
        </button>
        <button
          type="button"
          onClick={() => setLoginTab('staff')}
          className={`auth-tab ${loginTab === 'staff' ? 'active' : ''}`}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }}>badge</span>
          Personel Girişi
        </button>
      </div>

      {loginTab === 'seller' ? (
          totpRequired && totpSource === 'google' ? (
              /* Google ile kimlik doğrulandı, hesapta 2FA açık — sadece kod istenir. */
              <form onSubmit={onGoogleTotpSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="auth-field">
                      <label className="auth-label">Doğrulama Kodu (2FA)</label>
                      <div className="auth-input-wrap">
                          <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="one-time-code"
                              maxLength={6}
                              value={totpCode}
                              onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                              placeholder="123456"
                              autoFocus
                              className="auth-input"
                              style={{ borderColor: 'rgba(249,115,22,0.55)', fontSize: '18px', letterSpacing: '6px', textAlign: 'center' }}
                          />
                          <span className="material-symbols-outlined auth-input-icon">shield_lock</span>
                      </div>
                      <p className="auth-help">Google ile giriş yaptınız, hesabınızda iki adımlı doğrulama açık. Uygulamanızdaki 6 haneli kodu girin.</p>
                  </div>

                  <motion.button
                    type="submit"
                    className="auth-btn-primary"
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.97 }}
                  >
                      <span>Doğrula ve Giriş Yap</span>
                      <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>arrow_forward</span>
                  </motion.button>
                  <button type="button" onClick={cancelGoogleTotp} className="auth-link" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, textAlign: 'center' }}>
                      Vazgeç
                  </button>
              </form>
          ) : (
          <>
              <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="auth-field">
                      <label className="auth-label">{t('username')}</label>
                      <div className="auth-input-wrap">
                          <input
                              type="text"
                              name="username"
                              value={username}
                              onChange={onChange}
                              placeholder={t('username')}
                              className="auth-input"
                          />
                          <span className="material-symbols-outlined auth-input-icon">person</span>
                      </div>
                  </div>
                  <div className="auth-field">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '7px' }}>
                          <label className="auth-label" style={{ marginBottom: 0 }}>{t('password')}</label>
                          <Link to="/forgot-password" className="auth-link" style={{ fontSize: '12px' }}>{t('forgot_password')}</Link>
                      </div>
                      <div className="auth-input-wrap">
                          <input
                              type="password"
                              name="password"
                              value={password}
                              onChange={onChange}
                              placeholder="••••••••"
                              className="auth-input"
                          />
                          <span className="material-symbols-outlined auth-input-icon">key</span>
                      </div>
                  </div>

                  {totpRequired && (
                      <div className="auth-field">
                          <label className="auth-label">Doğrulama Kodu (2FA)</label>
                          <div className="auth-input-wrap">
                              <input
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="one-time-code"
                                  maxLength={6}
                                  value={totpCode}
                                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                                  placeholder="123456"
                                  autoFocus
                                  className="auth-input"
                                  style={{ borderColor: 'rgba(249,115,22,0.55)', fontSize: '18px', letterSpacing: '6px', textAlign: 'center' }}
                              />
                              <span className="material-symbols-outlined auth-input-icon">shield_lock</span>
                          </div>
                          <p className="auth-help">Doğrulama uygulamanızdaki 6 haneli kodu girin.</p>
                      </div>
                  )}

                  <motion.button
                    type="submit"
                    className="auth-btn-primary"
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.97 }}
                  >
                      <span>{totpRequired ? 'Doğrula ve Giriş Yap' : t('login')}</span>
                      <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>arrow_forward</span>
                  </motion.button>
              </form>

              <div className="auth-divider">
                  <div className="auth-divider-line"></div>
                  <span className="auth-divider-text">{t('or')}</span>
                  <div className="auth-divider-line"></div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <GoogleLogin
                      onSuccess={responseGoogle}
                      onError={() => alert('Google ile giriş yapılamadı.')}
                      text="continue_with"
                      shape="pill"
                      size="large"
                  />
              </div>
          </>
          )
      ) : (
          /* PERSONEL GİRİŞ FORMU */
          <form onSubmit={onStaffSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="auth-field">
                  <label className="auth-label">İşletme / Firma E-Posta Adresi *</label>
                  <div className="auth-input-wrap">
                      <input
                          type="email"
                          value={sellerEmail}
                          onChange={e => setSellerEmail(e.target.value)}
                          placeholder="isletme@firma.com"
                          required
                          className="auth-input"
                      />
                      <span className="material-symbols-outlined auth-input-icon">domain</span>
                  </div>
                  <p className="auth-help">Bağlı olduğunuz işletmenin kayıtlı e-posta adresini girin.</p>
              </div>

              <div className="auth-field">
                  <label className="auth-label">Şube / Mağaza *</label>
                  <div className="auth-input-wrap">
                      {loadingStores ? (
                          <div className="auth-input" style={{ display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.4)' }}>
                              Şubeler yükleniyor...
                          </div>
                      ) : storesList.length > 0 ? (
                          <select
                              name="store_id"
                              value={staffForm.store_id}
                              onChange={onStaffChange}
                              required
                              className="auth-select"
                          >
                              {storesList.map(st => (
                                  <option key={st.store_id} value={st.store_id}>{st.name}</option>
                              ))}
                          </select>
                      ) : (
                          <input
                              type="text"
                              disabled
                              placeholder={sellerEmail.trim() ? "Bu işletmeye ait aktif şube bulunamadı" : "Önce yukarıya İşletme E-Postasını girin..."}
                              className="auth-input"
                          />
                      )}
                      <span className="material-symbols-outlined auth-input-icon">storefront</span>
                  </div>
              </div>

              <div className="auth-field">
                  <label className="auth-label">Personel Kullanıcı Adı *</label>
                  <div className="auth-input-wrap">
                      <input
                          type="text"
                          name="username"
                          value={staffForm.username}
                          onChange={onStaffChange}
                          placeholder="Örn: garson_ahmet"
                          required
                          className="auth-input"
                      />
                      <span className="material-symbols-outlined auth-input-icon">badge</span>
                  </div>
              </div>

              <div className="auth-field">
                  <label className="auth-label">Personel Şifresi *</label>
                  <div className="auth-input-wrap">
                      <input
                          type="password"
                          name="password"
                          value={staffForm.password}
                          onChange={onStaffChange}
                          placeholder="••••••••"
                          required
                          className="auth-input"
                      />
                      <span className="material-symbols-outlined auth-input-icon">key</span>
                  </div>
              </div>

              <motion.button
                type="submit"
                disabled={staffLoading}
                className="auth-btn-primary"
                whileHover={staffLoading ? undefined : { y: -2 }}
                whileTap={staffLoading ? undefined : { scale: 0.97 }}
              >
                  <span>{staffLoading ? 'Giriş Yapılıyor...' : 'Personel Girişi Yap'}</span>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>login</span>
              </motion.button>
          </form>
      )}
    </AuthShell>
  );
};

export default LoginPage;
